import { useEffect, useState } from "react";
import { api, fmtEur, fmtDate, fileUrl, uploadFile, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Landmark, Search, Paperclip, FileText, X, FolderKanban } from "lucide-react";

const STRUMENTI = { voucher: "Voucher", avviso: "Avviso", conto_formazione: "Conto Formazione" };
const STR_CLS = {
  voucher: "bg-dusty-50 text-dusty-700 border-dusty-200",
  avviso: "bg-violet-50 text-violet-700 border-violet-200",
  conto_formazione: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const EMPTY = { denominazione: "", codice_fiscale: "", matricola_inps: "", strumento: "voucher", data_adesione: "", documenti: [], note: "", partner_id: "" };
const EMPTY_PRG = { id_progetto: "", descrizione: "", azienda: "", strumento: "voucher", importo_finanziato: "", costi_fissi: "", costi_variabili: "", royalty_partner: "", numero_fattura: "", data_documento: "", note: "", partner_id: "" };
const margine = (p) => (Number(p.importo_finanziato) || 0) - (Number(p.costi_fissi) || 0) - (Number(p.costi_variabili) || 0) - (Number(p.royalty_partner) || 0);

export default function Fondi() {
  const { user } = useAuth();
  const isOwner = user.role === "owner";       // vede il Margine Safe (utile)
  const isStaff = user.role !== "partner";     // gestisce l'assegnazione al partner
  const isCollab = user.role === "collaboratore"; // niente area contabilità progetti
  const [mainTab, setMainTab] = useState("aziende");
  const [items, setItems] = useState([]);
  const [progetti, setProgetti] = useState([]);
  const [partners, setPartners] = useState([]);
  const [tab, setTab] = useState("tutte");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [prgOpen, setPrgOpen] = useState(false);
  const [prg, setPrg] = useState(EMPTY_PRG);
  const [prgEditId, setPrgEditId] = useState(null);

  const load = () => api.get("/fondi").then((r) => setItems(r.data));
  const loadPrg = () => api.get("/progetti").then((r) => setProgetti(r.data));
  useEffect(() => {
    load();
    if (!isCollab) loadPrg();
    if (isStaff) api.get("/partners").then((r) => setPartners(r.data)).catch(() => {});
  }, [isStaff, isCollab]);

  // --- azienda aderente ---
  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (f) => {
    setForm({ denominazione: f.denominazione, codice_fiscale: f.codice_fiscale, matricola_inps: f.matricola_inps || "", strumento: f.strumento || "voucher", data_adesione: f.data_adesione || "", documenti: f.documenti || [], note: f.note || "", partner_id: f.partner_id || "" });
    setEditId(f.id); setOpen(true);
  };
  const onDocs = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name}: troppo grande (max 10 MB)`); continue; }
        const ref = await uploadFile(file);
        setForm((s) => ({ ...s, documenti: [...s.documenti, ref] }));
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setUploading(false); }
  };
  const save = async () => {
    if (!form.denominazione.trim() || !form.codice_fiscale.trim()) return toast.error("Denominazione e Codice Fiscale/P.IVA obbligatori");
    try {
      const payload = { ...form, ...(isStaff ? { partner_id: form.partner_id || null } : {}) };
      if (editId) await api.put(`/fondi/${editId}`, payload); else await api.post("/fondi", payload);
      toast.success(editId ? "Azienda aggiornata" : "Azienda registrata al fondo");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Eliminare questa azienda dal fondo?")) return; await api.delete(`/fondi/${id}`); toast.success("Azienda eliminata"); load(); };

  // --- progetti ---
  const openNewPrg = () => { setPrg(EMPTY_PRG); setPrgEditId(null); setPrgOpen(true); };
  const openEditPrg = (p) => {
    setPrg({ id_progetto: p.id_progetto, descrizione: p.descrizione || "", azienda: p.azienda || "", strumento: p.strumento || "voucher", importo_finanziato: p.importo_finanziato ?? "", costi_fissi: p.costi_fissi ?? "", costi_variabili: p.costi_variabili ?? "", royalty_partner: p.royalty_partner ?? "", numero_fattura: p.numero_fattura || "", data_documento: p.data_documento || "", note: p.note || "", partner_id: p.partner_id || "" });
    setPrgEditId(p.id); setPrgOpen(true);
  };
  const savePrg = async () => {
    if (!prg.id_progetto.trim()) return toast.error("L'ID progetto è obbligatorio");
    try {
      const payload = { ...prg, importo_finanziato: Number(prg.importo_finanziato) || 0, costi_fissi: Number(prg.costi_fissi) || 0, costi_variabili: Number(prg.costi_variabili) || 0, royalty_partner: Number(prg.royalty_partner) || 0, ...(isOwner ? { partner_id: prg.partner_id || null } : {}) };
      if (prgEditId) await api.put(`/progetti/${prgEditId}`, payload); else await api.post("/progetti", payload);
      toast.success(prgEditId ? "Progetto aggiornato" : "Progetto registrato");
      setPrgOpen(false); loadPrg();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delPrg = async (id) => { if (!window.confirm("Eliminare questo progetto?")) return; await api.delete(`/progetti/${id}`); toast.success("Progetto eliminato"); loadPrg(); };

  const q = search.trim().toLowerCase();
  const filtered = items.filter((f) => {
    if (tab !== "tutte" && f.strumento !== tab) return false;
    if (q && ![f.denominazione, f.codice_fiscale, f.matricola_inps].some((v) => (v || "").toLowerCase().includes(q))) return false;
    return true;
  });

  const mainTabs = isCollab ? [["aziende", "Aziende Aderenti"]] : [["aziende", "Aziende Aderenti"], ["progetti", "Progetti Finanziati"]];

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2"><Landmark className="h-7 w-7 text-dusty-600" /> Fondi — FonarCom</h1>
          <p className="text-sm text-slate-500 mt-1">Aziende aderenti al fondo{!isCollab && " e contabilità dei progetti finanziati"}.</p>
        </div>
        {mainTab === "aziende"
          ? <Button onClick={openNew} data-testid="add-fondo-btn" className="bg-dusty-600 hover:bg-dusty-700 text-white"><Plus className="h-4 w-4 mr-1.5" /> Registra azienda</Button>
          : isOwner && <Button onClick={openNewPrg} data-testid="add-progetto-btn" className="bg-dusty-600 hover:bg-dusty-700 text-white"><Plus className="h-4 w-4 mr-1.5" /> Nuovo progetto</Button>}
      </div>

      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {mainTabs.map(([k, l]) => (
          <button key={k} onClick={() => setMainTab(k)} data-testid={`main-tab-${k}`}
            className={`text-sm px-4 py-1.5 rounded-md transition-colors ${mainTab === k ? "bg-white text-dusty-700 shadow-sm font-medium" : "text-slate-500 hover:text-slate-700"}`}>{l}</button>
        ))}
      </div>

      {mainTab === "aziende" ? (
        <>
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
              {[["tutte", "Tutte"], ["voucher", "Voucher"], ["avviso", "Avviso"], ["conto_formazione", "Conto Formazione"]].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`fondo-tab-${k}`}
                  className={`text-sm px-3 py-1.5 rounded-md transition-colors ${tab === k ? "bg-white text-dusty-700 shadow-sm font-medium" : "text-slate-500 hover:text-slate-700"}`}>{l}</button>
              ))}
            </div>
            <div className="relative max-w-xs w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca denominazione, CF, matricola…" className="pl-9 bg-white" data-testid="fondo-search" />
            </div>
          </div>

          <Card className="border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                    <th className="py-3 px-4">Denominazione</th>
                    <th className="py-3 px-4">Codice Fiscale / P.IVA</th>
                    <th className="py-3 px-4">Matricola INPS</th>
                    <th className="py-3 px-4">Strumento</th>
                    <th className="py-3 px-4">Adesione</th>
                    <th className="py-3 px-4">Documenti</th>
                    {isStaff && <th className="py-3 px-4">Partner</th>}
                    <th className="py-3 px-4 text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((f) => (
                    <tr key={f.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`fondo-row-${f.id}`}>
                      <td className="py-3 px-4 font-medium text-slate-800">{f.denominazione}</td>
                      <td className="py-3 px-4 text-slate-600">{f.codice_fiscale}</td>
                      <td className="py-3 px-4 text-slate-600">{f.matricola_inps || "—"}</td>
                      <td className="py-3 px-4">{f.strumento ? <span className={`inline-flex text-xs px-2 py-0.5 rounded-full border ${STR_CLS[f.strumento]}`}>{STRUMENTI[f.strumento]}</span> : <span className="text-slate-300">—</span>}</td>
                      <td className="py-3 px-4 text-slate-600">{f.data_adesione ? fmtDate(f.data_adesione) : "—"}</td>
                      <td className="py-3 px-4">
                        {(f.documenti || []).length === 0 ? <span className="text-slate-300">—</span> : (
                          <div className="flex flex-wrap gap-1">
                            {f.documenti.map((d) => (
                              <a key={d.id} href={fileUrl(d.storage_path)} target="_blank" rel="noreferrer" title={d.original_filename}
                                className="inline-flex items-center gap-1 text-xs text-dusty-700 hover:underline" data-testid={`fondo-doc-${f.id}-${d.id}`}>
                                <FileText className="h-3.5 w-3.5" />PDF
                              </a>
                            ))}
                          </div>
                        )}
                      </td>
                      {isStaff && <td className="py-3 px-4 text-slate-600">{f.partner_nome}</td>}
                      <td className="py-3 px-4">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" onClick={() => openEdit(f)} data-testid={`edit-fondo-${f.id}`}><Pencil className="h-4 w-4 text-slate-500" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => del(f.id)} data-testid={`delete-fondo-${f.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={isStaff ? 8 : 7} className="py-12 text-center text-slate-400">Nessuna azienda aderente{tab !== "tutte" ? " per questo strumento" : ""}.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      ) : (
        <Card className="border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                  <th className="py-3 px-4">ID Progetto</th>
                  {isOwner && <th className="py-3 px-4">Partner</th>}
                  <th className="py-3 px-4">Azienda</th>
                  <th className="py-3 px-4 text-right">Importo finanziato</th>
                  <th className="py-3 px-4 text-right">Costi fissi</th>
                  <th className="py-3 px-4 text-right">Costi variabili</th>
                  <th className="py-3 px-4 text-right">Royalty partner</th>
                  <th className="py-3 px-4">N. Fattura</th>
                  <th className="py-3 px-4">Data doc.</th>
                  {isOwner && <th className="py-3 px-4 text-right">Margine Safe</th>}
                  {isOwner && <th className="py-3 px-4 text-right">Azioni</th>}
                </tr>
              </thead>
              <tbody>
                {progetti.map((p) => {
                  const m = margine(p);
                  return (
                    <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`progetto-row-${p.id}`}>
                      <td className="py-3 px-4 font-semibold text-slate-800">{p.id_progetto}{p.descrizione ? <div className="text-xs font-normal text-slate-400">{p.descrizione}</div> : null}</td>
                      {isOwner && <td className="py-3 px-4 text-slate-600">{p.partner_nome}</td>}
                      <td className="py-3 px-4 text-slate-600">{p.azienda || "—"}</td>
                      <td className="py-3 px-4 text-right text-slate-700">{fmtEur(p.importo_finanziato)}</td>
                      <td className="py-3 px-4 text-right text-slate-600">{fmtEur(p.costi_fissi)}</td>
                      <td className="py-3 px-4 text-right text-slate-600">{fmtEur(p.costi_variabili)}</td>
                      <td className="py-3 px-4 text-right text-dusty-700 font-medium">{fmtEur(p.royalty_partner)}</td>
                      <td className="py-3 px-4 text-slate-600" data-testid={`progetto-fattura-${p.id}`}>{p.numero_fattura || "—"}</td>
                      <td className="py-3 px-4 text-slate-600">{p.data_documento ? fmtDate(p.data_documento) : "—"}</td>
                      {isOwner && <td className={`py-3 px-4 text-right font-semibold ${m < 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmtEur(m)}</td>}
                      {isOwner && (
                        <td className="py-3 px-4">
                          <div className="flex justify-end gap-1">
                            <Button size="icon" variant="ghost" onClick={() => openEditPrg(p)} data-testid={`edit-progetto-${p.id}`}><Pencil className="h-4 w-4 text-slate-500" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => delPrg(p.id)} data-testid={`delete-progetto-${p.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {progetti.length === 0 && <tr><td colSpan={isOwner ? 11 : 8} className="py-12 text-center text-slate-400">Nessun progetto finanziato registrato.</td></tr>}
              </tbody>
            </table>
          </div>
          {!isOwner && <p className="text-xs text-slate-400 px-4 py-3">Riepilogo dei tuoi progetti finanziati: importo, royalty e documento fiscale da emettere per l'incasso.</p>}
        </Card>
      )}

      {/* Dialog azienda aderente */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="fondo-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editId ? "Modifica azienda aderente" : "Registra azienda al fondo FonarCom"}</DialogTitle>
            <DialogDescription>Anagrafica dell'azienda aderente, strumento e data di adesione.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1 max-h-[70vh] overflow-y-auto pr-1">
            <div><Label>Denominazione *</Label><Input value={form.denominazione} onChange={(e) => setForm({ ...form, denominazione: e.target.value })} className="mt-1.5" data-testid="fondo-denominazione" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Codice Fiscale / P.IVA *</Label><Input value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value })} className="mt-1.5" data-testid="fondo-cf" /></div>
              <div><Label>Matricola INPS</Label><Input value={form.matricola_inps} onChange={(e) => setForm({ ...form, matricola_inps: e.target.value })} className="mt-1.5" data-testid="fondo-matricola" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Strumento</Label>
                <Select value={form.strumento} onValueChange={(v) => setForm({ ...form, strumento: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="fondo-strumento"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="voucher">Voucher</SelectItem>
                    <SelectItem value="avviso">Avviso</SelectItem>
                    <SelectItem value="conto_formazione">Conto Formazione</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Data di adesione</Label><Input type="date" value={form.data_adesione} onChange={(e) => setForm({ ...form, data_adesione: e.target.value })} className="mt-1.5" data-testid="fondo-data-adesione" /></div>
            </div>
            <div>
              <Label>Documenti di adesione (PDF)</Label>
              <div className="mt-1.5">
                <label className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer" data-testid="fondo-doc-label">
                  <Paperclip className="h-4 w-4" /> {uploading ? "Carico…" : "Allega documento"}
                  <input type="file" multiple className="hidden" onChange={onDocs} accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" data-testid="fondo-doc-input" />
                </label>
              </div>
              {form.documenti.length > 0 && (
                <div className="mt-2 space-y-1">
                  {form.documenti.map((d) => (
                    <div key={d.id} className="flex items-center justify-between text-sm bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5">
                      <span className="inline-flex items-center gap-1.5 text-slate-600 truncate"><FileText className="h-3.5 w-3.5 shrink-0" />{d.original_filename}</span>
                      <button type="button" onClick={() => setForm((s) => ({ ...s, documenti: s.documenti.filter((x) => x.id !== d.id) }))} className="text-slate-400 hover:text-rose-500"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {isStaff && (
              <div>
                <Label>Partner (opzionale)</Label>
                <Select value={form.partner_id || "none"} onValueChange={(v) => setForm({ ...form, partner_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1.5" data-testid="fondo-partner"><SelectValue placeholder="Safe Consulting" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Safe Consulting</SelectItem>
                    {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Note</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5" data-testid="fondo-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={uploading} className="bg-dusty-600 hover:bg-dusty-700 text-white" data-testid="fondo-save">{editId ? "Salva" : "Registra"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog progetto */}
      <Dialog open={prgOpen} onOpenChange={setPrgOpen}>
        <DialogContent className="bg-white" data-testid="progetto-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><FolderKanban className="h-5 w-5 text-dusty-600" />{prgEditId ? "Modifica progetto" : "Nuovo progetto finanziato"}</DialogTitle>
            <DialogDescription>Contabilità del progetto finanziato assegnato al partner.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>ID Progetto *</Label><Input value={prg.id_progetto} onChange={(e) => setPrg({ ...prg, id_progetto: e.target.value })} className="mt-1.5" data-testid="prg-id" placeholder="es. PRG-001" /></div>
              <div><Label>Azienda</Label><Input value={prg.azienda} onChange={(e) => setPrg({ ...prg, azienda: e.target.value })} className="mt-1.5" data-testid="prg-azienda" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Strumento</Label>
                <Select value={prg.strumento} onValueChange={(v) => setPrg({ ...prg, strumento: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="prg-strumento"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="voucher">Voucher</SelectItem>
                    <SelectItem value="avviso">Avviso</SelectItem>
                    <SelectItem value="conto_formazione">Conto Formazione</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {isOwner && (
                <div>
                  <Label>Partner</Label>
                  <Select value={prg.partner_id || "none"} onValueChange={(v) => setPrg({ ...prg, partner_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="mt-1.5" data-testid="prg-partner"><SelectValue placeholder="Safe Consulting" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Safe Consulting</SelectItem>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Importo finanziato €</Label><Input type="number" value={prg.importo_finanziato} onChange={(e) => setPrg({ ...prg, importo_finanziato: e.target.value })} className="mt-1.5" data-testid="prg-importo" /></div>
              <div><Label>Royalty partner €</Label><Input type="number" value={prg.royalty_partner} onChange={(e) => setPrg({ ...prg, royalty_partner: e.target.value })} className="mt-1.5" data-testid="prg-royalty" /></div>
              <div><Label>Costi fissi €</Label><Input type="number" value={prg.costi_fissi} onChange={(e) => setPrg({ ...prg, costi_fissi: e.target.value })} className="mt-1.5" data-testid="prg-fissi" /></div>
              <div><Label>Costi variabili €</Label><Input type="number" value={prg.costi_variabili} onChange={(e) => setPrg({ ...prg, costi_variabili: e.target.value })} className="mt-1.5" data-testid="prg-variabili" /></div>
            </div>
            <div className="rounded-lg bg-dusty-50/60 border border-dusty-100 px-3 py-2.5 space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-dusty-700">Documento del partner per l'incasso</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>N. Fattura</Label><Input value={prg.numero_fattura} onChange={(e) => setPrg({ ...prg, numero_fattura: e.target.value })} className="mt-1.5" data-testid="prg-numero-fattura" placeholder="es. 2026/000045" /></div>
                <div><Label>Data documento</Label><Input type="date" value={prg.data_documento} onChange={(e) => setPrg({ ...prg, data_documento: e.target.value })} className="mt-1.5" data-testid="prg-data-documento" /></div>
              </div>
            </div>
            {isOwner && (
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 flex items-center justify-between text-sm">
                <span className="text-slate-500">Margine Safe (importo − costi − royalty)</span>
                <span className={`font-bold ${margine(prg) < 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmtEur(margine(prg))}</span>
              </div>
            )}
            <div><Label>Descrizione / Note</Label><Textarea value={prg.descrizione} onChange={(e) => setPrg({ ...prg, descrizione: e.target.value })} className="mt-1.5" data-testid="prg-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrgOpen(false)}>Annulla</Button>
            <Button onClick={savePrg} className="bg-dusty-600 hover:bg-dusty-700 text-white" data-testid="prg-save">{prgEditId ? "Salva" : "Registra"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
